import React, { useState } from 'react';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { type FormikHelpers } from 'formik';
import { useCreateApplicant } from './';
import { PAGE_TEXT, SignupForm, type SignupFormValues } from './components';
import { Alert, Loading, type AlertDetails } from '../../components';

const steps = [
  PAGE_TEXT.ACCOUNT_INFO,
  PAGE_TEXT.IDENTITY_TYPE,
  PAGE_TEXT.PERSONAL_INFO,
];

export const Signup: React.FunctionComponent = () => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const createApplicant = useCreateApplicant();

  const handleBack = (): void => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const isLastStep = activeStep === steps.length - 1;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [loading, setLoading] = useState(false);

  const handleLoading = async (
    callback: () => Promise<void>,
  ): Promise<void> => {
    setLoading(true);
    try {
      await callback();
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (
    values: SignupFormValues,
    { setSubmitting, setTouched }: FormikHelpers<SignupFormValues>,
  ): Promise<void> => {
    if (isLastStep) {
      const {
        telephoneNumber,
        disabilitySpecialType,
        disabilitySpecialDetail,
        emailVerificationCode,
        ...other
      } = values;
      await handleLoading(async () => {
        try {
          await createApplicant({
            variables: {
              user: {
                username: values.email,
                password: values.password,
                submissionCode: emailVerificationCode,
              },
              telephoneNumber: !telephoneNumber ? '' : telephoneNumber,
              ...(disabilitySpecialType !== 'NA' &&
              disabilitySpecialType !== undefined
                ? {
                    disabilitySpecial: {
                      disabilitySpecialType,
                      disabilitySpecialDetail,
                    },
                  }
                : { disabilitySpecial: null }),
              ...other,
            },
          });

          navigate('/signup/successful');
        } catch (error) {
          setOpenAlert(true);
          setAlertDetails({
            alertContent: `${PAGE_TEXT.SIGNUP_FAILED} ${
              (error as Error).message
            }`,
            alertType: 'error',
          });
          setActiveStep(prevActiveStep => prevActiveStep - 1);
        }
      });
    } else {
      setSubmitting(false);
      await setTouched({}, false);
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {loading && <Loading />}

      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Stepper
        activeStep={activeStep}
        style={{
          paddingTop: '50px',
          width: '50%',
          marginLeft: '25%',
        }}
      >
        {steps.map(label => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <SignupForm
        activeStep={activeStep}
        isLastStep={isLastStep}
        onBack={handleBack}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};
